/**
 * Sidebars (SPEC §3.2). Left: Journal, Tasks, Favorites, Tags, People, Companies, Recent; hidden
 * with ⌘\. Right: open nodes as editable panes and the calendar (SPEC §7.3: days with content
 * shaded, click to jump); resizable; ⌘⇧\ toggles it, ⌘⇧K / ⌘-click / ⌘⏎ open things in it.
 */
import { ChevronLeft, ChevronRight, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore, type PointerEvent as RPointerEvent, type ReactNode } from 'react'
import { mergedTarget, plainText, TAG, types, type Node } from '@solar/core'
import type { Pane } from '../client/store.ts'
import { dayTitle, todayKey } from './dates.ts'
import { useStore, useStructure, useUi } from './hooks.ts'
import { NodeView } from './NodeView.tsx'
import { navigate, useRoute } from './router.ts'

/** Re-render on any graph change, at most every `ms` (lists in the left sidebar). */
function useGraphTick(ms = 400): number {
  const store = useStore()
  const tick = useRef(0)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const subscribe = useCallback(
    (cb: () => void) => {
      const off = store.g.subscribe(() => {
        if (timer.current) return
        timer.current = setTimeout(() => {
          timer.current = null
          tick.current++
          cb()
        }, ms)
      })
      return () => {
        off()
        if (timer.current) clearTimeout(timer.current)
        timer.current = null
      }
    },
    [store, ms],
  )
  const get = useCallback(() => tick.current, [])
  return useSyncExternalStore(subscribe, get, get)
}

function Section({ title, children, more }: { title: string; children: ReactNode[]; more?: { href: string; count: number } }) {
  const key = `solar-left-${title}`
  const [open, setOpen] = useState(() => localStorage.getItem(key) !== '0')
  if (children.length === 0) return null
  return (
    <div className="lsec">
      <button
        className="link lsec-head"
        aria-expanded={open}
        onClick={() => {
          localStorage.setItem(key, open ? '0' : '1')
          setOpen(!open)
        }}
      >
        {title}
      </button>
      {open && (
        <>
          {children}
          {more && more.count > children.length && (
            <a className="nav more" href={more.href} onClick={(e) => (e.preventDefault(), navigate(more.href))}>
              All {more.count}
            </a>
          )}
        </>
      )}
    </div>
  )
}

function NavLink({ href, active, children }: { href: string; active?: boolean; children: ReactNode }) {
  return (
    <a className={active ? 'nav active' : 'nav'} href={href} onClick={(e) => (e.preventDefault(), navigate(href))}>
      {children}
    </a>
  )
}

export function LeftSidebar({ footer, force = false, onNavigate }: { footer: ReactNode; force?: boolean; onNavigate?: () => void }) {
  const store = useStore()
  useUi()
  const tick = useGraphTick()
  const route = useRoute()
  const here = route.name === 'node' ? route.id : null
  const lists = useMemo(() => {
    const titled = [...store.titles.nodes()]
    const person = mergedTarget(store.g, TAG.person)
    const company = mergedTarget(store.g, TAG.company)
    const byTitle = (a: Node, b: Node) => a.title!.localeCompare(b.title!)
    return {
      people: titled.filter((n) => n.tags.includes(person)).sort(byTitle),
      companies: titled.filter((n) => n.tags.includes(company)).sort(byTitle),
      favorites: titled.filter((n) => n.props['sys:favorite'] === true).sort(byTitle),
      types: types(store.g),
    }
  }, [store, tick])
  if (!store.sidebar.left && !force) return null
  const link = (n: Node) => (
    <NavLink key={n.id} href={`/n/${n.id}`} active={here === n.id}>
      {n.title}
    </NavLink>
  )
  const recent = store.recent.slice(0, 8).flatMap((v) => {
    if (v.kind === 'day') {
      return [
        <NavLink key={v.date} href={`/d/${v.date}`}>
          {dayTitle(v.date).title}
        </NavLink>,
      ]
    }
    const n = store.g.get(v.id)
    if (!n || n.deletedAt !== null) return []
    return [
      <NavLink key={n.id} href={`/n/${n.id}`} active={here === n.id}>
        {n.title ?? plainText(n.content).slice(0, 40)}
      </NavLink>,
    ]
  })
  return (
    <aside className={force ? 'left sheet-left' : 'left'} onClickCapture={(e) => (e.target as HTMLElement).closest('a') && onNavigate?.()}>
      <NavLink href="/" active={route.name === 'journal'}>
        Journal
      </NavLink>
      <NavLink href={`/n/${mergedTarget(store.g, TAG.task)}`} active={here === mergedTarget(store.g, TAG.task)}>
        Tasks
      </NavLink>
      <div className="lsecs">
        <Section title="Favorites">{lists.favorites.map(link)}</Section>
        <Section title="Tags">
          {lists.types.map((n) => (
            <NavLink key={n.id} href={`/n/${n.id}`} active={here === n.id}>
              #{n.title}
            </NavLink>
          ))}
        </Section>
        <Section title="People" more={{ href: `/n/${mergedTarget(store.g, TAG.person)}`, count: lists.people.length }}>
          {lists.people.slice(0, 8).map(link)}
        </Section>
        <Section title="Companies" more={{ href: `/n/${mergedTarget(store.g, TAG.company)}`, count: lists.companies.length }}>
          {lists.companies.slice(0, 8).map(link)}
        </Section>
        <Section title="Recent">{recent}</Section>
      </div>
      <div className="spacer" />
      {footer}
    </aside>
  )
}

// ------------------------------------------------------------------------------------ right

export function RightSidebar() {
  const store = useStore()
  useUi()
  const drag = useRef<{ x: number; w: number } | null>(null)
  if (!store.sidebar.open) return null
  const onDown = (e: RPointerEvent<HTMLDivElement>): void => {
    drag.current = { x: e.clientX, w: store.sidebar.width }
    e.currentTarget.setPointerCapture(e.pointerId)
  }
  const onMove = (e: RPointerEvent<HTMLDivElement>): void => {
    if (!drag.current) return
    const w = Math.min(700, Math.max(280, drag.current.w + drag.current.x - e.clientX))
    store.setSidebar({ width: Math.round(w) })
  }
  return (
    <aside className="right" style={{ width: store.sidebar.width }} aria-label="Right sidebar">
      <div className="resize" onPointerDown={onDown} onPointerMove={onMove} onPointerUp={() => (drag.current = null)} />
      <div className="panes">
        {store.sidebar.panes.length === 0 && <div className="pane-empty">⌘-click a link or press ⌘⇧K to open it here.</div>}
        {store.sidebar.panes.map((p) => (
          <PaneView key={p.key} pane={p} />
        ))}
      </div>
    </aside>
  )
}

function PaneView({ pane }: { pane: Pane }) {
  const store = useStore()
  const n = pane.kind === 'node' ? store.g.get(pane.id) : undefined
  const title = pane.kind === 'calendar' ? 'Calendar' : n ? (n.kind === 'journal' && n.date ? dayTitle(n.date).title : (n.title ?? plainText(n.content).slice(0, 40))) : 'Missing'
  return (
    <section className="pane" aria-label={title}>
      <header className="pane-head">
        {pane.kind === 'node' ? (
          <a href={`/n/${pane.id}`} onClick={(e) => (e.preventDefault(), navigate(n?.kind === 'journal' && n.date ? `/d/${n.date}` : `/n/${pane.id}`))}>
            {title}
          </a>
        ) : (
          <span>{title}</span>
        )}
        <button className="link" aria-label={`Close ${title}`} onClick={() => store.closePane(pane.key)}>
          <X size={14} />
        </button>
      </header>
      <div className="pane-body">{pane.kind === 'calendar' ? <Calendar /> : <NodeView id={pane.id} viewKey={`pane:${pane.key}`} />}</div>
    </section>
  )
}

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']

/** A month grid: days with content shaded, today ringed, click to open the day (SPEC §7.3). */
export function Calendar() {
  const store = useStore()
  useStructure()
  const today = todayKey()
  const [month, setMonth] = useState(today.slice(0, 7))
  const withContent = useMemo(() => new Set(store.journal.days().map((d) => d.date)), [store, store.g.structureVersion]) // eslint-disable-line react-hooks/exhaustive-deps
  const [y, m] = month.split('-').map(Number) as [number, number]
  const first = new Date(Date.UTC(y, m - 1, 1))
  const lead = (first.getUTCDay() + 6) % 7
  const days = new Date(Date.UTC(y, m, 0)).getUTCDate()
  const cells: (string | null)[] = [...Array(lead).fill(null), ...Array.from({ length: days }, (_, i) => `${month}-${String(i + 1).padStart(2, '0')}`)]
  const shift = (d: number): void => {
    const t = new Date(Date.UTC(y, m - 1 + d, 1))
    setMonth(`${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, '0')}`)
  }
  useEffect(() => setMonth(today.slice(0, 7)), [today])
  return (
    <div className="calendar">
      <div className="cal-head">
        <button className="link" aria-label="Previous month" onClick={() => shift(-1)}>
          <ChevronLeft size={16} />
        </button>
        <span>{first.toLocaleString('en', { month: 'long', year: 'numeric', timeZone: 'UTC' })}</span>
        <button className="link" aria-label="Next month" onClick={() => shift(1)}>
          <ChevronRight size={16} />
        </button>
      </div>
      <div className="cal-grid">
        {WEEKDAYS.map((w) => (
          <span key={w} className="cal-wd">
            {w}
          </span>
        ))}
        {cells.map((d, i) =>
          d === null ? (
            <span key={`x${i}`} />
          ) : (
            <button
              key={d}
              className={`cal-day${withContent.has(d) ? ' has' : ''}${d === today ? ' today' : ''}`}
              aria-label={dayTitle(d).title}
              onClick={() => navigate(d === today ? '/' : `/d/${d}`)}
            >
              {Number(d.slice(8))}
            </button>
          ),
        )}
      </div>
    </div>
  )
}
